import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BusinessVatConfigurationForm } from "../../../../../../components/form/business/vatconfiguration";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetBusiness } from "../../../../../../hooks/api/business/useGetBusiness";

import { usePostAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePostAdditionalBusinessDetails";
import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";
import { useGetBusinessList } from "../../../../../../hooks/api/business/useGetBusinessList";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Stack
} from "@vapor/react-material";
import { ExtendedTypography } from "@vapor/react-extended";
import { Close } from "@mui/icons-material";
import { useTranslation } from "@onefront/react-sdk";
import { ExtendedToggle, useWsPatch, useWsPost } from "@onefront/workspace-forms";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";

export const BusinessVatConfiguration = ({ setBusiness }) => {
    const { workspaceId, businessId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [bodyRequest, setBodyRequest] = useState<any>({
        vatPeriodicity: "NORMAL_MONTHLY",
        vatStatementRegistry: "ON_SALES",
        flatTaxAccountingRate: null,
        cashAccounting: false,
        specialVatRegime: "NO_SPECIAL_REGIME",
        vatDeductionRate: 0,
        vatExemptionManagementForExporter: "NO"
    });
    const [feedback, setFeedback] = useState<any>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [dialogMergePeriod, setDialogMergePeriod] = useState(false);
    const [mergePeriod, setMergePeriod] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [displayErrors, setDisplayErrors] = useState(false);

    const { fetch: fetchPostGeneralData, loading: loadingPostGeneralData } = useWsPost(
        `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/general-configuration`
    );
    const { fetch: fetchPatchGeneralData, loading: loadingPatchGeneralData } = useWsPatch(
        `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/general-configuration`,
        {}
    );

    const { data: business, loading: loadingGet, fetch: getBusiness } = useGetBusiness(workspaceId, businessId);

    const { data: businessList } = useGetBusinessList(workspaceId);

    const { loading: loadingPostVatConfiguration, fetch: postBusinessVatConfiguration } =
        usePostAdditionalBusinessDetails(workspaceId, businessId, "vat-configuration");

    const { loading: loadingPatchVatConfiguration, fetch: patchBusinessVatConfiguration } =
        usePatchAdditionalBusinessDetails(workspaceId, businessId, "vat-configuration", business?.vatConfiguration?.id);

    const handleChange = (val, key) => {
        setBodyRequest(prev => ({
            ...prev,
            [key]: val
        }));
    };

    const handleNavigateBack = useCallback(() => {
        const { pathname } = location;
        const newUrl = pathname.replace(/\/vatconfiguration$/, "/sectionals");
        navigate(newUrl);
    }, [location, navigate]);

    const checkMergePeriod = useMemo(() => {
        const { vatPeriodicity } = bodyRequest;
        const { mergePeriod } = businessList?.generalConfiguration ?? {};

        if (typeof mergePeriod !== "boolean") {
            return businessList?.business?.some((business: any) => {
                let bodyIsMonthly = vatPeriodicity?.includes("MONTHLY");
                let bodyIsQuarterly = vatPeriodicity?.includes("QUARTERLY");
                let businessIsMonthly = business?.vatConfiguration?.vatPeriodicity?.includes("MONTHLY");
                let businessIsQuarterly = business?.vatConfiguration?.vatPeriodicity?.includes("QUARTERLY");
                return (bodyIsMonthly && businessIsQuarterly) || (bodyIsQuarterly && businessIsMonthly);
            });
        } else {
            return false;
        }
    }, [bodyRequest, businessList]);

    const handleSave = useCallback(
        (mergePeriod?: boolean) => {
            const { id } = business?.vatConfiguration ?? {
                id: null
            };

            if (Object.keys(errors).some(key => errors[key])) {
                setDisplayErrors(errors);
            }

            if (mergePeriod) {
                setDialogMergePeriod(true);
                return;
            }

            const { vatExemptionForExporterStartingDate, vatExemptionManagementForExporter, ...rest } = bodyRequest;

            if (id) {
                patchBusinessVatConfiguration({
                    url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${businessId}/vat-configuration/${id}`,
                    data: {
                        ...rest,
                        vatExemptionManagementForExporter,
                        ...(vatExemptionManagementForExporter === "MOBILE_PLAFOND" && {
                            vatExemptionForExporterStartingDate
                        })
                    }
                }).then(res => {
                    navigate(`/details/${workspaceId}/business/wizard/${businessId}/ivaprorata`);
                });
            } else {
                postBusinessVatConfiguration({
                    data: {
                        ...rest,
                        vatExemptionManagementForExporter,
                        ...(vatExemptionManagementForExporter === "MOBILE_PLAFOND" && {
                            vatExemptionForExporterStartingDate
                        })
                    }
                }).then(res => {
                    navigate(`/details/${workspaceId}/business/wizard/${businessId}/ivaprorata`);
                });
            }
        },
        [
            bodyRequest,
            business,
            patchBusinessVatConfiguration,
            postBusinessVatConfiguration,
            navigate,
            workspaceId,
            businessId
        ]
    );

    const handleDialog = useCallback(setState => {
        setState(prevState => !prevState);
    }, []);

    const handleSaveMergePeriod = useCallback(() => {
        const { id } = businessList?.generalConfiguration ?? {};

        if (id) {
            fetchPatchGeneralData({
                url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/general-configuration/${id}`,
                data: {
                    mergePeriod: !mergePeriod
                }
            })
                .then(res => {
                    handleSave(false);
                })
                .catch(err => {
                    setFeedback(Feedback.ERROR);
                });
        } else {
            fetchPostGeneralData({
                data: {
                    mergePeriod: !mergePeriod
                }
            })
                .then(res => {
                    handleSave(false);
                })
                .catch(err => {
                    setFeedback(Feedback.ERROR);
                });
        }
    }, [mergePeriod, fetchPatchGeneralData, fetchPostGeneralData, handleSave, businessList, workspaceId]);

    useEffect(() => {
        if (businessId !== "create" && !business && !loadingGet) {
            getBusiness?.().then(res => {
                const { vatConfiguration } = res?.data ?? {};
                if (vatConfiguration) {
                    setBodyRequest(prev => ({
                        ...prev,
                        ...vatConfiguration,
                        id: undefined
                    }));
                }
                setBusiness(res?.data);
                setIsEditing(true);
            });
        }
    }, [businessId, getBusiness, business, loadingGet, setBusiness]);

    return (
        <>
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.vatconfiguration.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}
            {dialogMergePeriod && (
                <Dialog
                    open={dialogMergePeriod}
                    onClose={() => handleDialog(setDialogMergePeriod)}
                    aria-labelledby="alert-dialog-endDate-title"
                    aria-describedby="alert-dialog-endDate-description"
                    sx={{
                        "& .MuiDialog-paper": {
                            width: "100%",
                            maxWidth: "615px"
                        }
                    }}
                >
                    <DialogTitle>
                        {"Attenzione"}
                        <IconButton color="primary" onClick={() => handleDialog(setDialogMergePeriod)}>
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <Divider variant="alert" />
                    <DialogContent>
                        <Stack
                            spacing={3}
                            px={1}
                            sx={{
                                boxSizing: "border-box"
                            }}
                        >
                            <Box>
                                <DialogContentText id="alert-dialog-endDate-description">
                                    <ExtendedTypography variant="bodyLarge">
                                        {`L'attività che stai creando ha una periodicità IVA diversa dalle altre
                                            attività già presenti.`}
                                        <br />
                                        <br />
                                        {`
                                            Conferma per effettuare il cumulo/compensazione del risultato delle
                                            liquidazioni trimestrali con il risultato delle liquidazioni mensili
                                            corrispondenti ai mesi di marzo, giugno, settembre e dicembre (per i soli
                                            contribuenti di cui all'art. 74, comma 4).`}
                                    </ExtendedTypography>
                                </DialogContentText>
                            </Box>
                            <Box>
                                <ExtendedToggle
                                    label={"Per inibire il cumulo/compensazione attiva la scelta"}
                                    value={mergePeriod}
                                    name={"mergePeriod"}
                                    editing={true}
                                    onChange={e => setMergePeriod(e.target.checked)}
                                    toggleLabel={{
                                        label: "Esclusione cumulo/compensazione liquidazioni IVA",
                                        placement: "end"
                                    }}
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => handleDialog(setDialogMergePeriod)}>
                            Torna indietro
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={loadingPatchGeneralData || loadingPostGeneralData}
                            onClick={() => handleSaveMergePeriod()}
                        >
                            {(loadingPatchGeneralData || loadingPostGeneralData) && (
                                <CircularProgress size={20} style={{ marginRight: 8 }} color="inherit" />
                            )}
                            {t("general.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            <BusinessVatConfigurationForm
                bodyRequest={bodyRequest}
                displayErrors={displayErrors}
                setDisplayErrors={setDisplayErrors}
                handleChange={handleChange}
                isEditing={isEditing}
                onSave={() => handleSave(checkMergePeriod)}
                onCancel={handleNavigateBack}
                onError={(key, error) => {
                    setErrors(prev => ({
                        ...prev,
                        [key]: error
                    }));
                }}
                isNewBusiness={true}
                loading={loadingGet || loadingPostVatConfiguration || loadingPatchVatConfiguration}
            />
        </>
    );
};
